import { bodyCopyBold, Icon, Input, ISelectOption, Modal, Pullout, PulloutType, Select, Switch } from '@aon/aon-tws-common-ui';
import { DimensionType, EditState, SourceType } from '@aon/tws-common-ui-questions/build/lib/models/enum';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import {
	ICustomDimensionEditModel,
	IResponseOptionGroup,
	ISurveyDimension,
	ISurveyQuestionEditModel,
	ITranslationEditItem,
	IValidationErrorCollection,
} from '../../../models';
import { createSurveyDimension, editSurveyDimension } from '../../../store/SurveyDimensionsSlice';
import { TranslationHelper, ValidationErrorHelper } from '../../../utilities';
import useResponseOptionGroups from '../../../utilities/hooks/useResponseOptionGroups';
import { Col3Col9, ColFull, Label, Section } from '../../common';
import { TranslationEditor } from '../../translations/TranslationEditor';

export interface ICustomDimensionModalProps {
	isOpen: boolean;
	surveyId: number;
	dimensionToEdit?: ISurveyDimension;
	defaultSurveyCultureCode: string;
	supportedSurveyCultureCodes: string[];
	availableCultures: ISelectOption[];
	isSavingSurveyDimension: boolean;
	validationErrors?: IValidationErrorCollection;
	onModalClose: () => void;
	onSaveWithoutClose: (updatedDimension: ISurveyDimension) => void;
	isFollowUpQuestionOption: boolean;
}

const dimensionTypeSelectItems: ISelectOption[] = [
	{
		displayText: 'Dimension',
		name: DimensionType.Dimension.toString(),
	},
	{
		displayText: 'Index',
		name: DimensionType.Index.toString(),
	},
];
const WarningDiv = styled.div`
	color: red;
`;
const StyledIcon = styled(Icon).attrs({
	name: 'warning',
})`
	font-size: 16px;
	margin-right: 3px;
`;
const CustomizedDimensionModal = styled(Modal)`
	.modal-body {
		flex-direction: column;
		overflow: visible;
	}

	.tws-pullout-container {
		margin-top: 1rem;

		strong {
			${bodyCopyBold}
		}
	}

	.translation-warning {
		margin: auto;
		margin-bottom: 30px;
		width: 50%;

		.tws-pullout-container {
			margin-top: 0;
		}
	}
`;

const CustomDimensionModal = ({
	isOpen,
	surveyId,
	dimensionToEdit,
	defaultSurveyCultureCode,
	supportedSurveyCultureCodes,
	availableCultures,
	isSavingSurveyDimension,
	validationErrors,
	onModalClose,
	onSaveWithoutClose,
	isFollowUpQuestionOption,
}: ICustomDimensionModalProps) => {
	const validationHelper = new ValidationErrorHelper(validationErrors);
	const [dimension, setDimension] = useState<ICustomDimensionEditModel>({
		surveyId: surveyId,
		translations: TranslationHelper.initializeEditableTranslationItems(
			defaultSurveyCultureCode,
			availableCultures,
			supportedSurveyCultureCodes,
		),
		name: '',
		defaultCultureCode: defaultSurveyCultureCode,
		dimensionType: DimensionType.Dimension,
		isScored: false,
		sourceType: SourceType.Custom,
		surveyQuestionDimensions: [],
		surveyQuestions: [],
		ownerId: undefined,
		supportedCultures: supportedSurveyCultureCodes,
		sourceResponseOptionGroupId: undefined,
	});
	const [dimensionNameError, setDimensionNameError] = useState('');
	const [responseOptionGroupError, setResponseOptionGroupError] = useState('');
	const [disableIsDimensionScored, setDisableIsDimensionScored] = useState(false);
	const [userHasMadeChanges, setUserHasMadeChanges] = useState(false);
	const { isLoadingResponseOptionGroups, responseOptionGroups } = useResponseOptionGroups();
	const dispatch = useDispatch();

	const resetForm = useCallback(() => {
		setDimensionNameError('');
		setDisableIsDimensionScored(false);
		setUserHasMadeChanges(false);
		setDimension({
			surveyId: surveyId,
			translations: TranslationHelper.initializeEditableTranslationItems(
				defaultSurveyCultureCode,
				availableCultures,
				supportedSurveyCultureCodes,
			),
			name: '',
			defaultCultureCode: defaultSurveyCultureCode,
			dimensionType: DimensionType.Dimension,
			isScored: false,
			sourceType: SourceType.Custom,
			surveyQuestionDimensions: [],
			surveyQuestions: [],
			ownerId: undefined,
			supportedCultures: supportedSurveyCultureCodes,
			sourceResponseOptionGroupId: undefined,
		});
	}, [surveyId]);

	useEffect(() => {
		if (dimensionToEdit) {
			setDimension({
				surveyId: surveyId,
				translations: TranslationHelper.getEditableTranslations(dimensionToEdit.translations, defaultSurveyCultureCode),
				name: dimensionToEdit.name,
				defaultCultureCode: defaultSurveyCultureCode,
				surveyDimensionId: dimensionToEdit.dimensionId,
				dimensionType: dimensionToEdit.dimensionType,
				isScored: dimensionToEdit.isScored,
				sourceType: dimensionToEdit.sourceType,
				surveyQuestionDimensions: [],
				surveyQuestions: dimensionToEdit.surveyQuestions,
				ownerId: dimensionToEdit.ownerId,
				supportedCultures: supportedSurveyCultureCodes,
				sourceResponseOptionGroupId: dimensionToEdit.sourceResponseOptionGroupId,
			});
			if (dimensionToEdit.dimensionType === DimensionType.Index) {
				setDisableIsDimensionScored(true);
			}
		} else {
			resetForm();
		}
	}, [isOpen, dimensionToEdit, surveyId, defaultSurveyCultureCode, supportedSurveyCultureCodes, resetForm]);

	const getSelectOptionsFromResponseOptionGroups = (sourceResponseOptionGroups: IResponseOptionGroup[]): ISelectOption[] => {
		return sourceResponseOptionGroups.map((rog, index) => ({
			name: rog.responseOptionGroupId.toString(),
			displayText: rog.name,
			displayOrder: index,
		}));
	};

	const handleDimensionTypeChanged = (selectedDimensionType: string) => {
		setUserHasMadeChanges(true);
		if (selectedDimensionType === DimensionType.Index.toString()) {
			setDimension({
				...dimension,
				dimensionType: DimensionType.Index,
				isScored: true,
			});
			setDisableIsDimensionScored(true);
		} else {
			setDimension({
				...dimension,
				dimensionType: DimensionType.Dimension,
			});
			setDisableIsDimensionScored(false);
		}
	};

	const handleResponseOptionGroupChanged = (selectedResponseOptionGroupId: string) => {
		setUserHasMadeChanges(true);
		const parsedResponseOptionGroupId = parseInt(selectedResponseOptionGroupId, 10);
		let dimensionQuestions: ISurveyQuestionEditModel[] = [];
		if (dimensionToEdit) {
			dimensionQuestions =
				dimensionToEdit.surveyQuestions?.filter((q) => q.sourceResponseOptionGroupId === parsedResponseOptionGroupId) ?? [];
		}
		setDimension({
			...dimension,
			sourceResponseOptionGroupId: parsedResponseOptionGroupId,
			surveyQuestions: dimensionQuestions,
		});
	};

	const handleClose = () => {
		resetForm();
		onModalClose();
	};

	const validateModel = () => {
		let isValid = true;
		if (!dimension.name) {
			isValid = false;
			setDimensionNameError('Name is required.');
		} else {
			setDimensionNameError('');
		}

		if (dimension.isScored && !dimension.sourceResponseOptionGroupId) {
			isValid = false;
			setResponseOptionGroupError('A response option group is required for a scored dimension.');
		} else {
			setResponseOptionGroupError('');
		}

		return isValid;
	};

	const handleSubmit = (modalWillClose: boolean) => {
		if (!validateModel()) {
			return;
		}

		const dimensionPayload = {
			...dimension,
			sourceResponseOptionGroupId: dimension.isScored ? dimension.sourceResponseOptionGroupId : undefined,
			translations: [
				...dimension.translations,
				{
					cultureCode: defaultSurveyCultureCode,
					language: '',
					textValue: dimension.name,
					editState: dimensionToEdit ? EditState.Update : EditState.Add,
				},
			],
		};
		const onSuccess = modalWillClose ? onModalClose : onSaveWithoutClose;
		if (dimensionToEdit) {
			dispatch(
				editSurveyDimension({
					surveyDimension: dimensionPayload,
					onSuccess,
				}),
			);
		} else {
			dispatch(
				createSurveyDimension({
					surveyDimension: dimensionPayload,
					onSuccess,
				}),
			);
		}
	};

	return (
		<CustomizedDimensionModal
			isOpen={isOpen}
			contenthcentered={true}
			contentfullwidth={true}
			title={dimensionToEdit ? ContentConstants.TITLE_EDIT_CUSTOM_DIMENSION : ContentConstants.TITLE_CREATE_SURVEY_DIMENSIONS}
			onSubmit={() => handleSubmit(false)}
			primaryButtonText={ContentConstants.BUTTONS_SAVE}
			primaryButtonDisabled={!userHasMadeChanges || isSavingSurveyDimension || isFollowUpQuestionOption}
			onAlternatePrimaryButtonClick={() => handleSubmit(true)}
			alternatePrimaryButtonText={ContentConstants.BUTTONS_SAVE_AND_CLOSE}
			alternatePrimaryButtonDisabled={!userHasMadeChanges || isSavingSurveyDimension || isFollowUpQuestionOption}
			secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
			secondaryButtonDisabled={isSavingSurveyDimension}
			onSecondaryButtonClick={handleClose}
			onClosed={handleClose}
		>
			<Section
				id={'custom-dimesion-name-and-translations'}
				sectionHeader={ContentConstants.SECTIONHEADERS_CUSTOM_DIMENSION_MODAL_NAME_AND_TRANSLATIONS}
			>
				{dimensionToEdit && dimension.translations.length > 0 && dimensionToEdit.name !== dimension.name && (
					<div className='translation-warning'>
						<Pullout type={PulloutType.Warning}>
							<strong>{ContentConstants.WARNING_TRANSLATIONS_AFFECTED_HEADING.toUpperCase() + ': '}</strong>
							{ContentConstants.WARNING_TRANSLATIONS_AFFECTED_BODY.replace('{property}', 'name').replace('{model}', 'dimension')}
						</Pullout>
					</div>
				)}
				<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_DIMENSION_NAME}>
					<Input
						id='name'
						placeholder={ContentConstants.INPUT_PLACEHOLDER_DIMENSION_NAME}
						value={dimension.name}
						onChange={(e: any) => {
							setUserHasMadeChanges(true);
							setDimension({ ...dimension, name: e.target.value.trimStart() });
						}}
						error={dimensionNameError !== '' || validationHelper.Errors('name').length > 0}
						message={dimensionNameError || validationHelper.Errors('name')}
						aria-label='Dimension Name'
						aria-invalid={dimensionNameError !== '' || validationHelper.Errors('name').length > 0}
					/>
				</Col3Col9>
				{dimension.name.length > 0 && dimension.translations.length > 0 && (
					<>
						<ColFull>
							<Label size='md' className='tws-label'>
								{ContentConstants.SUBSECTIONHEADERS_DIMENSION_TRANSLATIONS}
							</Label>
						</ColFull>
						<TranslationEditor
							enableHtmlEditor={false}
							property={'Translations'}
							propertyDisplayName={'Translations'}
							hasDefaultTranslation={dimension.name.length > 0}
							translations={dimension.translations}
							showErrors={false}
							onChange={(updatedTranslations: ITranslationEditItem[]) => {
								setUserHasMadeChanges(true);
								setDimension({ ...dimension, translations: updatedTranslations });
							}}
						/>
					</>
				)}
			</Section>
			<Section id={'custom-dimension-info'} sectionHeader={ContentConstants.SECTIONHEADERS_CUSTOM_DIMENSION_MODAL_DIMENSION_INFO}>
				<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_DIMENSION_TYPE} labelInfo={ContentConstants.TOOLTIP_DIMENSION_TYPE_TEXT}>
					<Select
						items={dimensionTypeSelectItems}
						selectedValue={dimension.dimensionType?.toString()}
						selectSize='lg'
						onApply={(values?: string | string[] | undefined) => handleDimensionTypeChanged(values as string)}
						className='tws-dimension-type'
					/>
				</Col3Col9>
				<Col3Col9
					label={ContentConstants.SUBSECTIONHEADERS_DIMENSION_IS_SCORED}
					labelInfo={ContentConstants.TOOLTIP_DIMENSION_ISSCORED_TEXT}
				>
					<Switch
						checked={dimension.isScored}
						disabled={disableIsDimensionScored}
						getValue={(value: any) => {
							setUserHasMadeChanges(true);
							setDimension({ ...dimension, isScored: value });
							return value;
						}}
					/>
					{dimension.isScored && dimensionToEdit && !dimensionToEdit.isScored && (
						<Pullout type={PulloutType.Warning}>
							<strong>{ContentConstants.WARNING.toUpperCase() + ': '}</strong>
							{ContentConstants.WARNING_CUSTOM_DIMENSION_IS_SCORED_CHANGE}
						</Pullout>
					)}
				</Col3Col9>
				{dimension.isScored && (
					<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_DIMENSION_RESPONSE_OPTION}>
						<p>{ContentConstants.SECTION_DESCRIPTION_DIMENSION_RESPONSE_GROUP}</p>
						<Select
							isLoading={isLoadingResponseOptionGroups}
							displayText='Select response scale'
							selectedValue={dimension?.sourceResponseOptionGroupId?.toString()}
							selectSize='lg'
							items={getSelectOptionsFromResponseOptionGroups(responseOptionGroups)}
							onApply={(values?: string | string[] | undefined) => handleResponseOptionGroupChanged(values as string)}
							error={responseOptionGroupError.length > 0}
							message={responseOptionGroupError}
							className='tws-response-group'
						/>
						{dimensionToEdit &&
							dimensionToEdit.sourceResponseOptionGroupId &&
							dimensionToEdit.sourceResponseOptionGroupId != dimension.sourceResponseOptionGroupId && (
								<Pullout type={PulloutType.Warning}>
									<strong>{ContentConstants.WARNING.toUpperCase() + ': '}</strong>
									{ContentConstants.WARNING_CUSTOM_DIMENSION_RESPONSE_OPTION_CHANGE}
								</Pullout>
							)}
					</Col3Col9>
				)}
			</Section>
			{isFollowUpQuestionOption && (
				<WarningDiv>
					<StyledIcon name='warning'></StyledIcon>
					{ContentConstants.FOLLOWUP_QUESTION_DIMENSION_RENAME_WARNING}
				</WarningDiv>
			)}
		</CustomizedDimensionModal>
	);
};

export default CustomDimensionModal;
